import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { graphql, Link } from 'gatsby';

import colors from '../utils/colors';

const BlogBody = styled.div`
  margin-bottom: 60px;
  max-width: 750px;
  margin-left: auto;
  margin-right: auto;
`;

const BlogIntro = styled.p`
  font-size: 16px;
  line-height: 28px;
  color: ${colors.base2()};
  font-family: 'Open Sans', sans-serif;
  margin-top: 10px;
  margin-bottom: 40px;
  text-align: center;
`;

const PostCard = styled.div`
  margin-bottom: 32px;
  padding: 28px 32px;
  border: 1px solid ${colors.shade1()};
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  @media only screen and (max-width: 32em) {
    padding: 20px;
  }
`;

const PostDate = styled.span`
  font-size: 13px;
  font-family: 'Open Sans', sans-serif;
  color: ${colors.base3()};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const PostTitle = styled.h2`
  margin: 8px 0 12px 0;
  font-size: 20px;
  color: ${colors.base1()};

  a {
    color: ${colors.base1()};
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: ${colors.accent1()};
    }
  }
`;

const PostExcerpt = styled.p`
  font-size: 15px;
  line-height: 26px;
  color: ${colors.base2()};
  font-family: 'Open Sans', sans-serif;
  margin: 0 0 16px 0;
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-family: 'Open Sans', sans-serif;
  background-color: ${colors.accent3()};
  color: ${colors.accent1()};
`;

const ReadMore = styled(Link)`
  display: inline-block;
  margin-top: 14px;
  font-size: 14px;
  font-family: 'Open Sans', sans-serif;
  color: ${colors.accent1()};
  text-decoration: none;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: color 0.3s;

  &:hover {
    color: ${colors.accent2()};
  }
`;

class WritingPage extends React.Component {
  componentDidMount() {
    this.node.style.opacity = 0;
    window.requestAnimationFrame(() => {
      this.node.style.transition = 'opacity 1000ms ease-out';
      this.node.style.opacity = 1;
    });
  }

  getExcerpt(html) {
    if (typeof document === 'undefined') return '';
    const doc = document.createElement('html');
    doc.innerHTML = html;
    const paragraphs = doc.getElementsByTagName('p');
    for (let i = 0; i < paragraphs.length; i += 1) {
      const text = paragraphs[i].textContent.trim();
      if (text.length > 20) {
        const words = text.split(' ');
        return words.slice(0, Math.min(words.length, 40)).join(' ') + '...';
      }
    }
    return '';
  }

  render() {
    // Newest first. The GraphQL query already sorts by date_created DESC,
    // so this only drops the draft stub from the index.
    const posts = this.props.data.allMarkdownRemark.edges
      .filter(({ node }) => !node.frontmatter.draft);

    return (
      <BlogBody ref={(node) => { this.node = node; }}>
        <Helmet>
          <title>Writing - Kartikeya Sharma</title>
        </Helmet>
        <BlogIntro>
          Differential privacy, applied cryptography, and what breaks when the accounting is not checked.
        </BlogIntro>
        {posts.map(({ node }) => {
          const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
          const formattedDate = new Date(node.frontmatter.date_created).toLocaleDateString('en-US', dateOptions);

          return (
            <PostCard key={node.frontmatter.path}>
              <PostDate>{formattedDate}</PostDate>
              <PostTitle>
                <Link to={node.frontmatter.path}>{node.frontmatter.title}</Link>
              </PostTitle>
              <PostExcerpt>
                {node.frontmatter.summary || this.getExcerpt(node.html)}
              </PostExcerpt>
              <TagContainer>
                {node.frontmatter.tags.map((tag) => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </TagContainer>
              <ReadMore to={node.frontmatter.path}>Read full article &rarr;</ReadMore>
            </PostCard>
          );
        })}
      </BlogBody>
    );
  }
}

WritingPage.propTypes = {
  data: PropTypes.shape({
    allMarkdownRemark: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.object).isRequired,
    }).isRequired,
  }).isRequired,
};

export default WritingPage;
export const pageQuery = graphql`
  {
    allMarkdownRemark(sort: { fields: [frontmatter___date_created], order: DESC }) {
      edges {
        node {
          html
          frontmatter {
            date_created
            path
            tags
            title
            summary
            draft
          }
        }
      }
    }
  }
`;
